import classNames from 'classnames'
import { Icon } from 'components/common/Icon'
import { Popover } from 'components/common/Popover'
import dayjs from 'dayjs'
import { useUserProfile } from 'hooks'
import useTranslation from 'next-translate/useTranslation'
import { ReactNode } from 'react'
import { allTimezones, useTimezoneSelect } from 'react-timezone-select'

const headingClassName = 'font-body font-normal tracking-normal'

type ReleaseDetailsProps = {
  image?: ReactNode | string
  title: string
  subtitle: ReactNode | string
}

export const ReleaseDetails = ({
  image,
  title,
  subtitle,
}: ReleaseDetailsProps) => {
  return (
    <div className='flex space-x-4'>
      {image && (
        <>
          {typeof image === 'string' && (
            <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-primary-light'>
              <Icon name={image} size={16} className='text-primary-default' />
            </div>
          )}

          {typeof image !== 'string' && image}
        </>
      )}

      <div>
        <h3 className={classNames(headingClassName, 'text-xs text-cool-600')}>
          {title}
        </h3>

        <h4 className={classNames(headingClassName, 'text-base')}>
          {subtitle}
        </h4>
      </div>
    </div>
  )
}

export const ReleaseDate = ({
  exactReleaseDate,
  exactReleaseEndDate,
  tbd,
}: {
  exactReleaseDate?: string | null
  exactReleaseEndDate?: string | null
  tbd?: boolean
}) => {
  const { t } = useTranslation('release_calendar')
  const { data: userProfile } = useUserProfile()
  const timezone = userProfile?.releaseSettings?.timeZone || dayjs.tz.guess()

  const { options: timezoneOptions } = useTimezoneSelect({
    timezones: allTimezones,
    displayValue: 'GMT',
    labelStyle: 'original',
  })

  const timezoneOption = timezoneOptions.find(
    option => option.value === (timezone === 'GMT' ? 'Etc/GMT' : timezone)
  )

  const currentDate = dayjs().tz(timezone)
  const releaseDate = dayjs(exactReleaseDate).tz(timezone)
  const releaseEndDate = dayjs(exactReleaseEndDate).tz(timezone)

  return (
    <ReleaseDetails
      title={
        exactReleaseEndDate
          ? t('end_date')
          : releaseDate.format('MMMM DD, YYYY')
      }
      subtitle={
        <>
          {!tbd && (
            <>
              {exactReleaseEndDate && releaseEndDate
                ? releaseEndDate.format('MMMM DD, h:mm A')
                : releaseDate.format('h:mm A')}{' '}
              {timezoneOption && (
                <>
                  (GMT
                  {Boolean(timezoneOption.offset) && timezoneOption.offset && (
                    <>
                      {timezoneOption.offset < 0 && '-'}
                      {timezoneOption.offset > 0 && '+'}
                      {Math.abs(timezoneOption.offset)}
                    </>
                  )}
                  )
                </>
              )}
            </>
          )}

          {tbd && (
            <>
              {currentDate.isBefore(releaseDate, 'date') ? (
                t('time_tbd')
              ) : (
                <div className='flex items-center space-x-1'>
                  <span>{t('contact_gallery')}</span>

                  <Popover content={t('contact_gallery_directly')} />
                </div>
              )}
            </>
          )}
        </>
      }
      image='time'
    />
  )
}
